import React from 'react';
import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import Chip from '@mui/joy/Chip';
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';
import {
  Wrench as BuildIcon,
  Invoice as RequestQuoteIcon,
  FileText as DescriptionIcon,
  Camera as CameraAltIcon,
  Cloud as CloudIcon,
  Warning as WarningIcon,
  Clock as PendingIcon,
  CurrencyDollar as AttachMoneyIcon,
  Toolbox as HomeRepairServiceIcon,
  ChartBar as AssessmentIcon,
  Timer as TimerIcon,
  User as PersonIcon,
} from '@phosphor-icons/react';
import { format, formatDistanceToNow, isToday, isTomorrow } from 'date-fns';

// Context chip data types
export interface ContextChipData {
  id: string;
  type: ChipType;
  label: string;
  value?: string | number | Date;
  tooltip?: string;
  color?: ChipColor;
  icon?: React.ReactNode;
  onClick?: () => void;
  priority?: number; // Higher priority chips show first
}

export type ChipType = 
  | 'build_scheduled'
  | 'change_order'
  | 'insurance_doc'
  | 'estimate_sent'
  | 'payment_due'
  | 'inspection_scheduled'
  | 'weather_alert'
  | 'photos_requested'
  | 'adjuster_meeting'
  | 'work_order'
  | 'warranty_expiring'
  | 'sla_warning'
  | 'high_value'
  | 'vip_customer'
  | 'custom';

type ChipColor = 'primary' | 'success' | 'warning' | 'danger' | 'neutral';

// Chip configuration by type
const chipConfigs: Record<ChipType, {
  icon: React.ReactNode;
  defaultColor: ChipColor;
  formatValue?: (value: any) => string;
  getTooltip?: (value: any) => string;
}> = {
  build_scheduled: {
    icon: <BuildIcon />,
    defaultColor: 'primary',
    formatValue: (date: Date | string) => {
      const d = new Date(date);
      if (isToday(d)) return 'Today';
      if (isTomorrow(d)) return 'Tomorrow';
      return format(d, 'MMM d');
    },
    getTooltip: (date: Date | string) => 
      `Build scheduled for ${format(new Date(date), 'EEEE, MMMM d, yyyy')}`,
  },
  change_order: {
    icon: <RequestQuoteIcon />,
    defaultColor: 'warning',
    formatValue: (status: string) => status || 'Pending',
    getTooltip: (status: string) => 
      `Change order ${status || 'pending approval'}`,
  },
  insurance_doc: {
    icon: <DescriptionIcon />,
    defaultColor: 'success',
    formatValue: (type: string) => type || 'Uploaded',
    getTooltip: (type: string) => 
      `Insurance ${type || 'document'} uploaded`,
  },
  estimate_sent: {
    icon: <AttachMoneyIcon />,
    defaultColor: 'primary',
    formatValue: (date: Date | string) => 
      formatDistanceToNow(new Date(date), { addSuffix: true }),
    getTooltip: (date: Date | string) => 
      `Estimate sent ${format(new Date(date), 'PPp')}`,
  },
  payment_due: {
    icon: <AttachMoneyIcon />,
    defaultColor: 'warning',
    formatValue: (amount: number) => `$${amount.toLocaleString()}`,
    getTooltip: (amount: number) => 
      `Payment of $${amount.toLocaleString()} is due`,
  },
  inspection_scheduled: {
    icon: <AssessmentIcon />,
    defaultColor: 'primary',
    formatValue: (date: Date | string) => format(new Date(date), 'MMM d'),
    getTooltip: (date: Date | string) => 
      `Inspection scheduled for ${format(new Date(date), 'PPp')}`,
  },
  weather_alert: {
    icon: <CloudIcon />,
    defaultColor: 'danger',
    formatValue: (type: string) => type || 'Alert',
    getTooltip: (type: string) => 
      `Weather alert: ${type || 'Check conditions'}`,
  },
  photos_requested: {
    icon: <CameraAltIcon />,
    defaultColor: 'primary',
    formatValue: (count: number) => count ? `${count} needed` : 'Requested',
    getTooltip: (count: number) => 
      count ? `${count} photos requested` : 'Photos requested',
  },
  adjuster_meeting: {
    icon: <PersonIcon />,
    defaultColor: 'warning',
    formatValue: (date: Date | string) => {
      const d = new Date(date);
      if (isToday(d)) return 'Today';
      if (isTomorrow(d)) return 'Tomorrow';
      return format(d, 'MMM d');
    },
    getTooltip: (date: Date | string) => 
      `Adjuster meeting ${format(new Date(date), 'PPp')}`,
  },
  work_order: {
    icon: <HomeRepairServiceIcon />,
    defaultColor: 'primary',
    formatValue: (status: string) => status || 'Active',
    getTooltip: (status: string) => 
      `Work order ${status || 'in progress'}`,
  },
  warranty_expiring: {
    icon: <TimerIcon />,
    defaultColor: 'warning',
    formatValue: (days: number) => `${days} days`,
    getTooltip: (days: number) => 
      `Warranty expires in ${days} days`,
  },
  sla_warning: {
    icon: <WarningIcon />,
    defaultColor: 'danger',
    formatValue: (hours: number) => `${hours}h remaining`,
    getTooltip: (hours: number) => 
      `SLA deadline in ${hours} hours`,
  },
  high_value: {
    icon: <AttachMoneyIcon />,
    defaultColor: 'success',
    formatValue: (amount: number) => `$${(amount / 1000).toFixed(0)}k+`,
    getTooltip: (amount: number) => 
      `High value job: $${amount.toLocaleString()}`,
  },
  vip_customer: {
    icon: <PersonIcon />,
    defaultColor: 'success',
    formatValue: () => 'VIP',
    getTooltip: () => 'VIP Customer - Priority handling',
  },
  custom: {
    icon: <PendingIcon />,
    defaultColor: 'neutral',
    formatValue: (value: any) => String(value || ''),
    getTooltip: (value: any) => String(value || ''),
  },
};

// Individual context chip component
interface ContextChipProps {
  chip: ContextChipData;
  size?: 'sm' | 'md';
  showTooltip?: boolean;
}

export const ContextChip: React.FC<ContextChipProps> = ({ 
  chip, 
  size = 'sm',
  showTooltip = true 
}) => {
  const config = chipConfigs[chip.type];
  const icon = chip.icon || config.icon;
  const color = chip.color || config.defaultColor;
  
  const displayValue = chip.value !== undefined && config.formatValue 
    ? config.formatValue(chip.value)
    : chip.label;
    
  const tooltip = chip.tooltip || 
    (chip.value !== undefined && config.getTooltip ? config.getTooltip(chip.value) : '');

  const chipElement = (
    <Chip
      size={size}
      variant="soft"
      color={color}
      startDecorator={icon}
      onClick={chip.onClick}
      sx={{
        cursor: chip.onClick ? 'pointer' : 'default',
        '&:hover': chip.onClick ? {
          opacity: 0.8,
        } : {},
      }}
    >
      {displayValue}
    </Chip>
  );

  if (showTooltip && tooltip) {
    return (
      <Tooltip title={tooltip} arrow placement="top">
        {chipElement}
      </Tooltip>
    );
  }

  return chipElement;
};

// Context chips container
interface ContextChipsProps {
  chips: ContextChipData[];
  maxVisible?: number;
  size?: 'sm' | 'md';
  showTooltips?: boolean;
}

const ContextChips: React.FC<ContextChipsProps> = ({
  chips,
  maxVisible = 3,
  size = 'sm',
  showTooltips = true,
}) => {
  // Sort chips by priority (higher first)
  const sortedChips = [...chips].sort((a, b) => 
    (b.priority || 0) - (a.priority || 0)
  );

  const visibleChips = sortedChips.slice(0, maxVisible);
  const hiddenCount = sortedChips.length - maxVisible;

  return (
    <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
      {visibleChips.map((chip) => (
        <ContextChip 
          key={chip.id} 
          chip={chip} 
          size={size}
          showTooltip={showTooltips}
        />
      ))}
      
      {hiddenCount > 0 && (
        <Tooltip
          title={
            <Box>
              <Typography level="body-xs" fontWeight="bold" sx={{ mb: 1 }}>
                Additional context:
              </Typography>
              <Stack spacing={0.5}>
                {sortedChips.slice(maxVisible).map((chip) => {
                  const config = chipConfigs[chip.type];
                  const displayValue = chip.value !== undefined && config.formatValue 
                    ? config.formatValue(chip.value)
                    : chip.label;
                  return (
                    <Typography key={chip.id} level="body-xs">
                      • {displayValue}
                    </Typography>
                  );
                })}
              </Stack>
            </Box>
          }
          arrow
          placement="top"
        >
          <Chip
            size={size}
            variant="outlined"
            color="neutral"
          >
            +{hiddenCount} more
          </Chip>
        </Tooltip>
      )}
    </Stack>
  );
};

// Helper function to generate context chips from job/lead data
export const generateContextChips = (data: {
  job?: any;
  lead?: any;
  messages?: any[];
}): ContextChipData[] => {
  const chips: ContextChipData[] = [];
  
  // Job-related chips
  if (data.job) {
    // Build scheduled
    if (data.job.build_date) {
      chips.push({
        id: 'build-scheduled',
        type: 'build_scheduled',
        label: 'Build',
        value: data.job.build_date,
        priority: 10,
      });
    }

    // Change order
    if (data.job.has_change_order) {
      chips.push({
        id: 'change-order',
        type: 'change_order',
        label: 'Change Order',
        value: data.job.change_order_status || 'Pending',
        priority: 9,
      });
    }

    // High value job
    if (data.job.total_value > 50000) {
      chips.push({
        id: 'high-value',
        type: 'high_value',
        label: 'High Value',
        value: data.job.total_value,
        priority: 8,
      });
    }

    // Inspection scheduled
    if (data.job.inspection_date) {
      chips.push({
        id: 'inspection',
        type: 'inspection_scheduled',
        label: 'Inspection',
        value: data.job.inspection_date,
        priority: 7,
      });
    }

    // Work order status
    if (data.job.work_order_status) {
      chips.push({
        id: 'work-order',
        type: 'work_order',
        label: 'Work Order',
        value: data.job.work_order_status,
        priority: 6,
      });
    }

    // Weather alert
    if (data.job.weather_alert) {
      chips.push({
        id: 'weather',
        type: 'weather_alert',
        label: 'Weather',
        value: data.job.weather_alert_type,
        priority: 11, // High priority for weather
      });
    }

    // Payment due
    if (data.job.payment_due_amount) {
      chips.push({
        id: 'payment-due',
        type: 'payment_due',
        label: 'Payment Due',
        value: data.job.payment_due_amount,
        priority: 9,
      });
    }
  }

  // Lead-related chips
  if (data.lead) {
    // VIP customer
    if (data.lead.is_vip) {
      chips.push({
        id: 'vip',
        type: 'vip_customer',
        label: 'VIP',
        priority: 12, // Highest priority
      });
    }

    // Insurance documents
    if (data.lead.insurance_docs_uploaded) {
      chips.push({
        id: 'insurance',
        type: 'insurance_doc',
        label: 'Insurance',
        value: data.lead.insurance_doc_type || 'Uploaded',
        priority: 5,
      });
    }

    // Estimate sent
    if (data.lead.estimate_sent_date) {
      chips.push({
        id: 'estimate',
        type: 'estimate_sent',
        label: 'Estimate',
        value: data.lead.estimate_sent_date,
        priority: 4,
      });
    }

    // Photos requested
    if (data.lead.photos_requested) {
      chips.push({
        id: 'photos',
        type: 'photos_requested',
        label: 'Photos',
        value: data.lead.photos_requested_count || 0,
        priority: 6,
      });
    }

    // Adjuster meeting
    if (data.lead.adjuster_meeting_date) {
      chips.push({
        id: 'adjuster',
        type: 'adjuster_meeting',
        label: 'Adjuster',
        value: data.lead.adjuster_meeting_date,
        priority: 8,
      });
    }

    // Warranty expiring
    if (data.lead.warranty_days_remaining && data.lead.warranty_days_remaining < 90) {
      chips.push({
        id: 'warranty',
        type: 'warranty_expiring',
        label: 'Warranty',
        value: data.lead.warranty_days_remaining,
        priority: 7,
      });
    }

    // SLA warning
    if (data.lead.sla_hours_remaining && data.lead.sla_hours_remaining < 24) {
      chips.push({
        id: 'sla',
        type: 'sla_warning',
        label: 'SLA',
        value: data.lead.sla_hours_remaining,
        priority: 11, // High priority for SLA
      });
    }
  }

  return chips;
};

export default ContextChips;